import { Browser } from "@capacitor/browser";
import { useMutation } from "@tanstack/react-query";
import cx from "classnames";
import dayjs from "dayjs";
import { useEffect, useReducer, useState } from "react";
import {
  Link,
  type LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  CURRENT_ORDER_QUERY_KEY,
  invalidateCurrentOrderDetails,
} from "@/modules/cart/useCartDetails";
import { FormattedString } from "@/utils/FormattedStrings";
import { get, post } from "api";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import ChevronRight from "atoms/icon/icons/chevron_right.svg";
import AddedToCartCheck from "atoms/icon/icons/ic_added_to_cart_check_ol.svg";
import QrCode from "atoms/icon/icons/qr_code.svg";
import { ImageCarousel } from "atoms/ImageCarousel/ImageCarousel";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { TextButton } from "atoms/text-button/TextButton";
import { AccordionList } from "molecules/accordion-list/AccordionList";
import { ExpandableItems } from "molecules/expandable-items/ExpandableItems";
import { ActionModal } from "molecules/modal-window/action-modal/ActionModal";
import TopNav from "molecules/navbars/TopNav";
import { AddedToCartModal } from "organisms/added-to-cart-modal/AddedToCartModal";
import { ModalSelectorList } from "organisms/selection-modals/modal-selector-list/ModalSelectorList";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import {
  getBookAppointmentAction,
  getProductTitle,
} from "services/HelperService";
import { waitObj } from "toolbox/Promise";
import pluralize from "utils/pluralize";

import { BuyAgainModal } from "./BuyAgainModal";
import {
  QuantitySelectorModal,
  type TreatmentProps,
} from "./QuantitySelectorModal";
import { TreatmentSelectorModal } from "./TreatmentSelectorModal";

type Action =
  | { type: "package"; packageId: string | null; priceId: string | null }
  | { type: "price"; priceId: string }
  | { type: "open"; modal: "cart" }
  | { type: "close" };

type State = {
  packageId: string | null | undefined;
  priceId: string | null | undefined;
  modal: "cart" | null;
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "package":
      return { ...state, packageId: action.packageId, priceId: action.priceId };

    case "price":
      return { ...state, priceId: action.priceId };

    case "open":
      return { ...state, modal: action.modal };

    case "close":
      return { ...state, modal: null };
  }
};

const load = async ({
  active,
  isFromMembership,
  comingFromScan,
  packageId,
  locationId,
}: {
  active: boolean;
  isFromMembership: boolean;
  comingFromScan: boolean;
  packageId: string;
  locationId?: string;
}) => {
  const platformLocation = queryClient.fetchQuery(["/platform-location"], () =>
    get("/platform-location")
  );

  const platformUserPackage = queryClient.fetchQuery(
    ["/platform-user-packages/{packageId}", { packageId, locationId }],
    () =>
      get("/platform-user-packages/{packageId}", {
        isFromMembership,
        active,
        packageId,
        locationId: locationId ?? undefined,
      })
  );

  const data = await waitObj({ platformUserPackage, platformLocation });

  const id = data.platformUserPackage.packageListingId;
  const platformUserPackageLocationId = data.platformUserPackage.locationId;
  let packageData = null;
  let listing = null;
  let platformUserPackageLocation = null;

  if (id) {
    packageData = queryClient.fetchQuery(
      ["/listings/{id}/packages", { id, excludeLocation: true }],
      () =>
        get("/listings/{id}/packages", {
          id,
          excludeLocation: true,
        })
    );

    listing = queryClient.fetchQuery(
      ["/listings/{id}", { id, excludeLocation: true }],
      () => get("/listings/{id}", { id, excludeLocation: true })
    );
  }

  if (platformUserPackageLocationId) {
    platformUserPackageLocation = queryClient.fetchQuery(
      [
        "/platform-location/{locationId}",
        { locationId: platformUserPackageLocationId },
      ],
      () =>
        get("/platform-location/{locationId}", {
          locationId: platformUserPackageLocationId,
        })
    );
  }

  const authClaims = queryClient.fetchQuery(["/auth/claims"], () =>
    get("/auth/claims")
  );
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const isAdminImpersonation = authClaims.then(
    (response) => !!response?.claims.adminUserId
  );

  const checkInAvailable = queryClient.fetchQuery(["/user/check-in"], () =>
    get("/user/check-in")
  );

  const user = await queryClient.fetchQuery(["/auth/user"], () => get("/user"));

  const locations = queryClient.ensureQueryData({
    queryKey: ["platform/locations"],

    queryFn: async () => {
      const response = await get("/platform/locations", {});
      return {
        items: response.items || [],
      };
    },
  });

  return waitObj({
    platformUserPackage,
    platformLocation,
    platform,
    packageId,
    packageData,
    listing,
    locations,
    platformUserPackageLocation,
    comingFromScan,
    isAdminImpersonation,
    userLocation: user.platformLocation,
    checkInAvailable,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const treatmentLoader: LoaderFunction = async ({
  params: { packageId },
  request: { url },
}) => {
  if (!packageId) {
    return redirect("..");
  }

  const searchParams = new URL(url).searchParams;
  const active = searchParams.get("active") === "true";
  const isFromMembership = searchParams.get("isFromMembership") === "true";
  const comingFromScan = searchParams.get("comingFromScan") === "true";
  const locationIdQueryValue = searchParams.get("locationId");
  const locationId =
    locationIdQueryValue === null ||
    locationIdQueryValue === "null" ||
    locationIdQueryValue === "undefined"
      ? undefined
      : locationIdQueryValue;

  return await load({
    active,
    isFromMembership,
    comingFromScan,
    packageId,
    locationId,
  });
};

const singlePackage = (packageData: any) =>
  packageData.length === 1 ? packageData[0].id : null;

const singlePrice = (packageData: any, packageId: string | null) => {
  const $package = packageData.find((p: any) => p.id === packageId);
  return $package && $package.packagePrices.length === 1
    ? $package.packagePrices[0].id
    : null;
};

/**
 * All types of modals that can be shown on the TreatmentPage.
 * Only one can be shown at a time.
 * Let's use a union type to represent them.
 */
type TreatmentPageModalType =
  | "quantity"
  | "buyAgain"
  | "location"
  | "treatmentSelector"
  | "treatmentQuantity"
  | "redeemSuccess"
  | null;

export const TreatmentPage = () => {
  const navigate = useNavigate();

  const {
    platformUserPackage,
    platformLocation,
    platform,
    packageData,
    packageId,
    listing,
    locations,
    platformUserPackageLocation,
    comingFromScan,
    isAdminImpersonation,
    userLocation,
    checkInAvailable,
  } = useLoaderData() as LoaderData;

  const [activeModal, setActiveModal] = useState<TreatmentPageModalType>(null);
  const hideActiveModal = () => setActiveModal(null);

  const isV3Enabled = true;
  const isSelectPatientLocationFlagOn = useFeatureFlag("selectPatientLocation");
  const isPatientLocationDifferentFromListing = userLocation?.id
    ? !listing?.locationIds?.includes(userLocation?.id || "")
    : true;
  const patientLocationOption =
    isSelectPatientLocationFlagOn || isPatientLocationDifferentFromListing;

  const isAllLocations =
    listing?.locationIds?.length === locations.items.length;

  const locationDisplayText =
    listing?.locationIds
      ?.map((id) => locations.items.find((loc) => loc.id === id)?.name)
      .filter((name) => name)
      .join(", ") ||
    userLocation?.name ||
    "location";

  const locationText = isAllLocations ? (
    <>
      Redeemable at <strong>all locations</strong>
    </>
  ) : (
    <>
      Redeemable at the <strong>{locationDisplayText}</strong> location
    </>
  );

  const isPackageAvailable = platformUserPackage.packageListingActive;
  const [searchParams, setSearchParams] = useSearchParams();
  const isUnitedDerm = useFeatureFlag("unitedDerm");
  const firstPackageItem = platformUserPackage.platformUserPackageItemModels[0];
  const [treatmentDetails, setTreatmentDetails] = useState<TreatmentProps>({
    id: firstPackageItem?.productId ?? "",
    name: firstPackageItem?.name ?? "",
    remainingQuantity: firstPackageItem?.remainingQuantity ?? 0,
  });

  const skipScanInOffice = !checkInAvailable || isAdminImpersonation;

  const treatmentSelectorModalButtonText = skipScanInOffice
    ? "Redeem now"
    : "Next";
  const $package = packageData?.find(({ id }) => id === packageId);
  let previousSelectedAmount =
    $package?.packagePrices.find(
      ({ quantity }) => quantity === platformUserPackage.totalTreatmentAmount
    ) || null;

  const [state, dispatch] = useReducer(reducer, {
    packageId: isPackageAvailable ? singlePackage(packageData) : null,
    priceId:
      isPackageAvailable && previousSelectedAmount
        ? previousSelectedAmount.id
        : isPackageAvailable
        ? singlePrice(packageData, singlePackage(packageData))
        : null,
    modal: null,
  });

  const price =
    $package?.packagePrices.find(({ id }) => id === state.priceId) || null;
  const cost =
    price?.price ?? $package?.baseCost ?? (listing?.baseCost || null);

  // only relevant when admins are impersonating, ENG-2038
  // https://app.asana.com/1/1200167319116526/project/1209918357392839/task/1210160254752052
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (comingFromScan) {
      setActiveModal("redeemSuccess");
    }
  }, [comingFromScan]);

  const closeRedeemSuccessModal = () => {
    setActiveModal(null);
    if (searchParams.has("comingFromScan")) {
      searchParams.delete("comingFromScan");
      setSearchParams(searchParams);
    }
  };

  const { bookAppointmentUrl, phoneNumber, email } =
    platformUserPackageLocation ?? platformLocation;

  const bookAppointmentHref = getBookAppointmentAction(
    bookAppointmentUrl,
    phoneNumber,
    email
  );

  const isPackage =
    platformUserPackage.platformUserPackageItemModels.length > 1;

  const handleTreatmentSelectorOnClick = (id: string) => {
    setActiveModal(null);
    const selectedTreatment =
      platformUserPackage.platformUserPackageItemModels.find(
        ({ productId }) => productId === id
      );
    setTreatmentDetails({
      id: selectedTreatment?.productId ?? "",
      name: selectedTreatment?.name ?? "",
      remainingQuantity: selectedTreatment?.remainingQuantity ?? 0,
    });
    if (
      selectedTreatment?.remainingQuantity &&
      selectedTreatment.remainingQuantity > 1
    ) {
      setActiveModal("treatmentQuantity");
    } else {
      handleTreatmentQuantityOnClick(1);
    }
  };

  const [selectedQuantity, setSelectedQuantity] = useState<number | null>(null);

  const handleTreatmentQuantityOnClick = (quantity: number) => {
    setActiveModal(null);
    // remove patientLocationOption when selectPatientLocation
    // is removed.
    if (patientLocationOption && skipScanInOffice) {
      // if we only have one location available, skip the location flow
      if (listing?.locationIds?.length === 1 || locations.items.length === 1) {
        handleRedeeming(quantity, treatmentDetails.id);
        return;
      }
      setSelectedQuantity(quantity);
      setActiveModal("location");
    } else {
      // Normal user flow: directly redeem (scan flow)
      if (isPackage) {
        handleRedeeming(quantity, treatmentDetails.id);
      } else {
        handleRedeeming(quantity);
      }
    }
  };

  const isPreviousTreatment =
    platformUserPackage.remainingTreatmentAmount === 0;

  const handleRedeemOnClick = () => {
    if (!isPackage) {
      const itemModel = platformUserPackage.platformUserPackageItemModels[0];
      if (itemModel.remainingQuantity > 1) {
        setActiveModal("treatmentQuantity");
      } else {
        handleTreatmentQuantityOnClick(1);
      }
      return;
    }

    setActiveModal("treatmentSelector");
  };

  const handleRedeeming = (
    quantity: number,
    productId?: string,
    locationId?: string
  ) => {
    const productIdInput =
      productId ||
      platformUserPackage.platformUserPackageItemModels[0].productId;

    if (skipScanInOffice) {
      redeemMutation.mutate({
        quantity,
        productId: productIdInput,
        ...(locationId && { overrideLocationId: locationId }),
      });
    } else {
      handleScanToRedeemNavigation(quantity, productIdInput);
    }
  };

  const handleRedeemWithLocation = (locationId: string) => {
    if (selectedQuantity === null) return;

    if (isPackage) {
      handleRedeeming(selectedQuantity, treatmentDetails.id, locationId);
    } else {
      handleRedeeming(selectedQuantity, undefined, locationId);
    }

    // Clean up
    setSelectedQuantity(null);
    setSelectedLocationId(null);
  };

  const isFromMembership = platformUserPackage.type === "MembershipTreatment";

  const handleScanToRedeemNavigation = (
    quantity: number,
    productId: string
  ) => {
    navigate("/scan-redeem-treatment-in-location", {
      state: {
        packageId: platformUserPackage.packageId,
        isFromMembership: isFromMembership.toString(),
        productId,
        remainingAmount:
          platformUserPackage.remainingTreatmentAmount - quantity,
        quantity,
        locationId: platformUserPackage.locationId,
      },
    });
  };

  const redeemMutation = useMutation({
    mutationFn: ({
      quantity,
      productId,
      overrideLocationId,
    }: {
      quantity: number;
      productId: string;
      overrideLocationId?: string;
    }) =>
      post("/platform-user-package-items/redeem-in-app", {
        packageId: platformUserPackage.packageId,
        locationId: overrideLocationId ?? platformUserPackage.locationId,
        isFromMembership,
        productId,
        quantity,
      }),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [
          "/platform-user-packages/{packageId}",
          { packageId, locationId: platformUserPackage.locationId },
        ],
      });

      const isPackageActive = !!(
        platformUserPackage.remainingTreatmentAmount - variables.quantity
      );
      navigate(
        `/wallet/treatments/${platformUserPackage.packageId}?isFromMembership=${isFromMembership}&active=${isPackageActive}&comingFromScan=true&locationId=${platformUserPackage.locationId}`
      );
    },
  });
  const activeAndAvailable = listing?.active;

  const availableLocations = listing?.locationIds?.length
    ? locations.items.filter((location) =>
        listing.locationIds.includes(location.id)
      )
    : locations.items; // If no restrictions, show all locations

  const isPartnerEmrBookingEnabled =
    platform.platformSettings?.partnerEmrBookingEnabled;

  const isBookableService =
    platformUserPackage.platformUserPackageItemModels.length === 1 &&
    isPartnerEmrBookingEnabled;

  return (
    <div className="flex h-full flex-col bg-two">
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => {
          navigate("/wallet/treatments");
        }}
      >
        <span className="flex items-center">
          <h1 className="text-xl">Wallet</h1>
        </span>
      </TopNav>

      {isV3Enabled ? (
        /* ========================================
           V3 DESIGN (MODERN LUXURY DESIGN)
           ======================================== */
        <div className="flex flex-1 flex-col overflow-y-auto bg-white">
          {/* Header Section */}
          <div className="flex flex-col items-center gap-2 pb-4 pt-6">
            <div className="text-center font-serif text-h2 text-primary">
              {platformUserPackage &&
                getProductTitle(
                  platformUserPackage.productName,
                  platformUserPackage.variantName
                )}
            </div>
          </div>

          {/* Image Section */}
          {platformUserPackage.imageUrl && (
            <div className="relative z-0 flex flex-col items-center pb-2">
              <ImageCarousel
                slides={[
                  {
                    src: platformUserPackage.imageUrl,
                    alt:
                      platformUserPackage &&
                      getProductTitle(
                        platformUserPackage.productName,
                        platformUserPackage.variantName
                      ),
                  },
                ]}
                productName={
                  platformUserPackage &&
                  getProductTitle(
                    platformUserPackage.productName,
                    platformUserPackage.variantName
                  )
                }
              />
            </div>
          )}

          {/* Info Section */}
          <div className="px-4">
            <div className="flex flex-col items-center pb-4">
              <div className="text-center text-body2 text-secondary">
                Purchased{" "}
                {dayjs(platformUserPackage.purchasedDate).format(
                  "MMMM D, YYYY"
                )}
              </div>
              {patientLocationOption && (
                <div className="text-center text-body2 text-secondary">
                  {locationText}
                </div>
              )}
            </div>
          </div>

          {!isBookableService && (
            <div className="px-4">
              <div className="flex flex-col items-center pb-4">
                <div className="text-center text-bold1 text-primary">
                  {platformUserPackage.remainingTreatmentAmount} of{" "}
                  {platformUserPackage.totalTreatmentAmount}{" "}
                  {pluralize(
                    platformUserPackage.totalTreatmentAmount,
                    isPackage
                      ? "treatment"
                      : platformUserPackage.packageUnitType?.singularUnitType ??
                          "",
                    isPackage
                      ? "treatments"
                      : platformUserPackage.packageUnitType?.pluralUnitType ??
                          ""
                  )}{" "}
                  remaining
                </div>

                {isPackage &&
                  platformUserPackage.remainingTreatmentAmount > 0 && (
                    <ul className="mt-2 list-none text-center text-body2 text-secondary">
                      {platformUserPackage.platformUserPackageItemModels.map(
                        ({ productId, name, remainingQuantity }) => {
                          if (remainingQuantity === 0) {
                            return null;
                          }

                          return (
                            <li key={productId}>
                              {remainingQuantity}x {name}
                            </li>
                          );
                        }
                      )}
                    </ul>
                  )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {!isPreviousTreatment && (
            <div className="px-4 pb-4">
              {!platformUserPackage.packageZenotiId && (
                <div className="mb-2">
                  <Button size="large" fullWidth onClick={handleRedeemOnClick}>
                    {!skipScanInOffice && (
                      <Icon size="xsmall" svg={QrCode} color="white" />
                    )}
                    <div className="ml-1.5">
                      {skipScanInOffice
                        ? "Redeem now"
                        : "Scan in office to redeem"}
                    </div>
                  </Button>
                </div>
              )}
              {!isPartnerEmrBookingEnabled && bookAppointmentHref && (
                <Button
                  size="large"
                  style="secondary"
                  fullWidth
                  onClick={() => Browser.open({ url: bookAppointmentHref })}
                >
                  {!isUnitedDerm ? "Book appointment" : "Request appointment"}
                </Button>
              )}
            </div>
          )}

          {isPreviousTreatment && (
            <div className="px-4 pb-4">
              {!activeAndAvailable && (
                <div className="mb-4 text-center text-body2 text-secondary">
                  This service is no longer available, browse our shop now to
                  find similar services
                </div>
              )}
              <Button
                size="large"
                style={!activeAndAvailable ? "secondary" : "primary"}
                fullWidth
                onClick={() =>
                  !activeAndAvailable
                    ? navigate("/shop")
                    : navigate(`/pdp/package/${listing?.id}`)
                }
              >
                {!activeAndAvailable ? "Browse shop" : "Buy again"}
              </Button>
            </div>
          )}

          {/* Description Section - Only show for non-packages */}
          {listing?.description && !isPackage && (
            <div className="p-4">
              <div className="text-h4 mb-2 font-serif font-semibold text-primary">
                What&apos;s Included
              </div>
              <div className="[&_p]:text-body2">
                <FormattedString>{listing.description}</FormattedString>
              </div>
            </div>
          )}

          {/* Package Items - What's Included */}
          {isPackage && (
            <div className="p-4">
              <div className="text-h4 mb-2 font-serif font-semibold text-primary">
                What&apos;s Included
              </div>
              <div className="flex flex-col gap-2">
                {platformUserPackage.platformUserPackageItemModels.map(
                  ({
                    productId,
                    name,
                    description,
                    totalQuantity,
                    remainingQuantity,
                  }) => (
                    <div
                      key={productId}
                      className="flex w-full items-center overflow-hidden rounded-lg border border-l-8 border-l-gray-200 bg-white shadow-subtle"
                    >
                      <div className="flex flex-1 flex-col overflow-hidden py-2 pl-4 pr-2">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="flex items-baseline gap-1">
                            {totalQuantity > 1 && (
                              <span className="font-serif text-body2 font-semibold text-brand-color">
                                {totalQuantity}x
                              </span>
                            )}
                            <div className="truncate font-serif text-body2 font-semibold">
                              {name}
                            </div>
                          </div>
                          {remainingQuantity < totalQuantity && (
                            <DescriptiveTag size="small" color="secondary">
                              {remainingQuantity === 0
                                ? "REDEEMED"
                                : `${
                                    totalQuantity - remainingQuantity
                                  }x REDEEMED`}
                            </DescriptiveTag>
                          )}
                        </div>
                        {description && (
                          <div className="line-clamp-2 text-body3 text-secondary">
                            {description}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* Key Treatment Info - Accordion */}
          {!!platformUserPackage.expectations?.length && (
            <div className="-mx-2">
              <AccordionList
                items={platformUserPackage.expectations
                  .filter((exp) => exp.title && exp.description)
                  .map((exp) => ({
                    title: exp.title,
                    children: (
                      <div className="text-justify text-body2 text-primary">
                        {exp.description}
                      </div>
                    ),
                    titleClassName:
                      "font-serif text-h4 font-semibold text-primary",
                  }))}
              />
            </div>
          )}

          {/* More Info Link */}
          {!!platformUserPackage.packageListingId &&
            !isPatientLocationDifferentFromListing && (
              <div className="my-6 flex justify-center">
                <Link
                  to={`/pdp/package/${platformUserPackage.packageListingId}`}
                >
                  <TextButton endIconSVG={ChevronRight}>
                    More {isPackage ? "package" : "treatment"} info
                  </TextButton>
                </Link>
              </div>
            )}
        </div>
      ) : (
        /* ========================================
           V2 DESIGN (LEGACY)
           ======================================== */
        <div className="bg-white px-4 py-8 text-center">
          <div className="text-primar mb-2 font-serif text-h1">
            {platformUserPackage &&
              getProductTitle(
                platformUserPackage.productName,
                platformUserPackage.variantName
              )}
          </div>
          <div className="mx-2 mb-6 text-body2 text-secondary">
            <span className="block">
              Purchased{" "}
              {dayjs(platformUserPackage.purchasedDate).format("MMMM D YYYY")}
            </span>
            {patientLocationOption && (
              <span className="block">{locationText}</span>
            )}
          </div>
          {platformUserPackage.imageUrl && (
            <div className="mx-2 mb-6">
              <img
                className={cx(
                  "block aspect-[4/3] h-full w-full snap-center rounded object-cover",
                  isPreviousTreatment && "opacity-90"
                )}
                src={platformUserPackage.imageUrl}
                height={86}
                alt="Treatment"
              />
            </div>
          )}

          {listing?.description && (
            <>
              <p className="text-center text-sub1 uppercase text-primary">
                What&apos;s Included
              </p>
              <div className="mx-2 mb-6 max-h-64 overflow-y-auto p-3 pb-12 text-left text-body2 text-secondary">
                <FormattedString>{listing.description}</FormattedString>
              </div>
            </>
          )}

          <div
            className={cx(
              "text-bold1 text-primary",
              isPackage && platformUserPackage.remainingTreatmentAmount > 0
                ? "mb-2"
                : "mb-6"
            )}
          >
            {platformUserPackage.remainingTreatmentAmount} of{" "}
            {platformUserPackage.totalTreatmentAmount}{" "}
            {pluralize(
              platformUserPackage.totalTreatmentAmount,
              isPackage
                ? "treatment"
                : platformUserPackage.packageUnitType?.singularUnitType ?? "",
              isPackage
                ? "treatments"
                : platformUserPackage.packageUnitType?.pluralUnitType ?? ""
            )}{" "}
            remaining
          </div>

          {isPackage && platformUserPackage.remainingTreatmentAmount > 0 && (
            <ul className="mb-6 text-body1 text-secondary">
              {platformUserPackage.platformUserPackageItemModels.map(
                ({ productId, name, remainingQuantity }) => {
                  if (remainingQuantity === 0) {
                    return null;
                  }

                  return (
                    <li key={productId}>
                      {remainingQuantity}x {name}
                    </li>
                  );
                }
              )}
            </ul>
          )}
          {!isPreviousTreatment && (
            <>
              {!platformUserPackage.packageZenotiId && (
                <div className="mb-3">
                  <Button size="large" fullWidth onClick={handleRedeemOnClick}>
                    {!skipScanInOffice && (
                      <Icon size="xsmall" svg={QrCode} color="white" />
                    )}
                    <div className="ml-1.5">
                      {skipScanInOffice
                        ? "Redeem now"
                        : "Scan in office to redeem"}
                    </div>
                  </Button>
                </div>
              )}
              {!isPartnerEmrBookingEnabled && bookAppointmentHref && (
                <Button
                  size="large"
                  style="secondary"
                  fullWidth
                  onClick={() => Browser.open({ url: bookAppointmentHref })}
                >
                  {!isUnitedDerm ? "Book appointment" : "Request appointment"}
                </Button>
              )}
            </>
          )}
          {isPreviousTreatment && (
            <div className="mt-6">
              {!activeAndAvailable && (
                <h1 className="pb-5 text-secondary">
                  This service is no longer available, browse our shop now to
                  find similar services
                </h1>
              )}
              <Button
                size="large"
                style={!activeAndAvailable ? "secondary" : "primary"}
                fullWidth
                onClick={() =>
                  !activeAndAvailable
                    ? navigate("/shop")
                    : navigate(`/pdp/package/${listing?.id}`)
                }
              >
                {!activeAndAvailable ? "Browse shop" : "Buy again"}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* V2 ADDITIONAL SECTIONS (LEGACY) */}
      {!isV3Enabled && isPackage && (
        <div className="bg-one px-6 pb-6">
          <div className="my-6 text-center text-sub1 text-brand-color">
            WHAT IS INCLUDED
          </div>

          <ExpandableItems
            items={platformUserPackage.platformUserPackageItemModels.map(
              ({ name, description, totalQuantity, remainingQuantity }) => ({
                id: name,
                title: (
                  <div className="flex items-center justify-between">
                    <div>
                      {totalQuantity}x {name}
                    </div>
                    {remainingQuantity < totalQuantity && (
                      <DescriptiveTag size="small" color="secondary">
                        {remainingQuantity === 0
                          ? "REDEEMED"
                          : `${totalQuantity - remainingQuantity}x REDEEMED`}
                      </DescriptiveTag>
                    )}
                  </div>
                ),
                description,
              })
            )}
          />
        </div>
      )}

      {!isV3Enabled && !!platformUserPackage.expectations?.length && (
        <div className="bg-two">
          <div className="my-6 text-center text-sub1 text-brand-color">
            KEY TREATMENT INFO
          </div>
          <div className="mx-4 space-y-2">
            {platformUserPackage.expectations.map(
              ({ id, title, description }) => (
                <div className="rounded bg-white p-6" key={id}>
                  <div className="mb-3 text-center text-sub2 uppercase text-primary">
                    {title}
                  </div>
                  <div className="text-body1 text-secondary">{description}</div>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {!isV3Enabled &&
        !!platformUserPackage.packageListingId &&
        !isPatientLocationDifferentFromListing && (
          <div className="my-6 flex justify-center">
            <Link to={`/pdp/package/${platformUserPackage.packageListingId}`}>
              <TextButton endIconSVG={ChevronRight}>
                More {isPackage ? "package" : "treatment"} info
              </TextButton>
            </Link>
          </div>
        )}

      <ActionModal
        open={activeModal === "redeemSuccess"}
        size="small"
        description={
          isAdminImpersonation ? (
            ""
          ) : (
            <div className="px-2">
              You don&apos;t need to do anything else, just take a seat or speak
              to the front desk if you have any questions.
            </div>
          )
        }
        firstButtonLabel="Close"
        headerIconSvg={AddedToCartCheck}
        onClose={closeRedeemSuccessModal}
        onFirstButtonClick={closeRedeemSuccessModal}
        title="Treatment redeemed!"
      />

      {activeModal === "treatmentSelector" && (
        <TreatmentSelectorModal
          primaryOnClick={handleTreatmentSelectorOnClick}
          primaryLabel={treatmentSelectorModalButtonText}
          onClose={hideActiveModal}
          treatments={platformUserPackage.platformUserPackageItemModels.reduce(
            (acc: { id: string; name: string }[], curr) => {
              if (curr.remainingQuantity === 0) {
                return acc;
              }

              return [
                ...acc,
                {
                  id: curr.productId,
                  name: curr.name,
                  quantity: curr.remainingQuantity,
                },
              ];
            },
            []
          )}
        />
      )}

      {activeModal === "treatmentQuantity" && (
        <QuantitySelectorModal
          primaryOnClick={handleTreatmentQuantityOnClick}
          primaryLabel={skipScanInOffice ? "Continue" : "Scan now"}
          onClose={hideActiveModal}
          treatment={treatmentDetails}
        />
      )}

      {cost !== null &&
        cost > 0 &&
        isPackageAvailable &&
        listing &&
        activeModal === "buyAgain" && (
          <BuyAgainModal
            open={activeModal === "buyAgain"}
            onClose={hideActiveModal}
            title={platformUserPackage.variantName ?? ""}
            onClickDropdownSelector={() => setActiveModal("quantity")}
            price={price}
            package={$package}
            platform={platform}
            platformUserPackage={platformUserPackage}
            cost={cost}
            listing={listing}
            isPriceSet={!!state.priceId}
            onClickAddToCart={async () => {
              if (!$package || !price) {
                return;
              }

              await post("/orders/current/lineitems", {
                packageListingId: listing.id,
                packageId: $package.id,
                packagePriceId: price.id,
              });

              queryClient.invalidateQueries({
                queryKey: ["/orders/current/summary"],
              });

              dispatch({ type: "open", modal: "cart" });
              setActiveModal(null);
            }}
          />
        )}

      {isPackageAvailable &&
        activeModal === "quantity" &&
        listing &&
        $package && (
          <ModalSelectorList
            size="medium"
            open={true}
            onClose={hideActiveModal}
            title={
              "NUMBER OF " +
              `${
                $package?.packageUnitType.pluralUnitType?.toUpperCase() ??
                "UNITS"
              }`
            }
            type="products"
            items={$package.packagePrices.map(
              ({ id, price, membershipPrice, quantity }) => ({
                id: id,
                priceOrResults: price,
                membershipPrice: membershipPrice,
                title:
                  quantity === 1
                    ? `${quantity} ${
                        $package?.packageUnitType.singularUnitType ?? "unit"
                      }`
                    : `${quantity} ${
                        $package?.packageUnitType.pluralUnitType ?? "units"
                      }`,
              })
            )}
            checkedItems={state.priceId ? [state.priceId] : []}
            onSelect={(priceId) => {
              dispatch({ type: "price", priceId });
              previousSelectedAmount = null;
            }}
            firstButtonLabel="Add to cart"
            onFirstButtonClick={async (locationId?: string) => {
              if (!$package || !price) {
                return;
              }

              await post("/orders/current/lineitems", {
                packageListingId: listing.id,
                packageId: $package.id,
                packagePriceId: price.id,
                ...(locationId && { locationId }), // Only include locationId if provided
              });
              invalidateCurrentOrderDetails();

              dispatch({ type: "open", modal: "cart" });
              setActiveModal(null);
            }}
          />
        )}

      {isPackageAvailable && $package && price && (
        <AddedToCartModal
          title={$package.name}
          open={state.modal === "cart"}
          subtitle={
            price.quantity === 1
              ? `${price.quantity} ${$package.packageUnitType.singularUnitType}`
              : `${price.quantity} ${$package.packageUnitType.pluralUnitType}`
          }
          cost={price.price}
          membership={null}
          onClose={() => {
            dispatch({ type: "close" });
            setActiveModal(null);
          }}
        />
      )}

      {activeModal === "location" && (
        <ModalSelectorList
          size="medium"
          open={true}
          onClose={() => {
            hideActiveModal();
            // Clean up redemption state
            setSelectedQuantity(null);
            setSelectedLocationId(null);
          }}
          title="SELECT LOCATION"
          type="location"
          items={availableLocations.map((location) => ({
            id: location.id,
            title: location.name,
          }))}
          checkedItems={selectedLocationId ? [selectedLocationId] : []}
          onSelect={setSelectedLocationId}
          firstButtonLabel="Redeem now"
          onFirstButtonClick={() => {
            if (!selectedLocationId) return;
            handleRedeemWithLocation(selectedLocationId);
            setActiveModal(null);
          }}
        />
      )}
    </div>
  );
};
